import { useState } from 'react';
import { Message, MessageInput, AppError } from 'models';
import { Moment } from 'moment';
import { buildFilePath } from 'helpers/firebase';
import { MediaType } from 'models/forms';
import Messages from 'services/firebase/database/messages';
import useStorage from './useStorage';

interface MessagesHookResponse {
  messages: Message[];
  loading: boolean;
  error: null | AppError;
  getAll: () => Promise<void>;
  add: (message: MessageInput) => Promise<boolean>;
  remove: (id: string, imageURL: string) => Promise<boolean>;
  update: (message: Message, newMessage: MessageInput) => Promise<boolean>;
  getDates: () => Promise<Moment[]>;
}

const useMessages = (): MessagesHookResponse => {
  const { remove: removeFile, upload } = useStorage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null as null | AppError);
  const [messages, setMessages] = useState([] as Message[]);

  const getAllMessages = async (): Promise<void> => {
    const result = await Messages.getAll<Message>();

    switch (result.type) {
      case 'success':
        setMessages(result.data);
        break;
      case 'error':
        setError(result.error);
        break;
      default:
    }
    setLoading(false);
  };

  const getAllDates = async (): Promise<Moment[]> => {
    const result = await Messages.getDates();
    return result.type === 'success' ? result.data : [];
  };

  const addMessage = async (message: MessageInput): Promise<boolean> => {
    const imageURL = await upload(message.imageURL as Blob, MediaType.IMAGE);
    if (imageURL) {
      const newMessage = {
        date: message.date,
        text: message.text,
        imageURL,
      };
      const result = await Messages.add<MessageInput>(newMessage);
      return result.type === 'success';
    }
    return false;
  };

  const updateMessage = async (
    message: Message,
    newMessage: MessageInput,
  ): Promise<boolean> => {
    let { imageURL } = message;
    if (newMessage.imageURL && typeof newMessage.imageURL !== 'string') {
      const newURL = await upload(newMessage.imageURL, MediaType.IMAGE);
      if (!newURL) return false;
      imageURL = newURL;
    }
    const msgToUpdate = {
      date: newMessage.date,
      text: newMessage.text,
      imageURL,
    };
    const result = await Messages.update<MessageInput>(message.id, msgToUpdate);
    return result.type === 'success';
  };

  const removeMessage = async (
    id: string,
    imageURL: string,
  ): Promise<boolean> => {
    const path = buildFilePath(imageURL, MediaType.IMAGE);
    const removedFile = await removeFile(path);
    if (removedFile) {
      const result = await Messages.remove(id);
      return result.type === 'success';
    }
    return false;
  };

  return {
    messages,
    loading,
    error,
    getAll: getAllMessages,
    add: addMessage,
    remove: removeMessage,
    update: updateMessage,
    getDates: getAllDates,
  };
};

export default useMessages;
